import React from 'react';
import { AiOutlineInstagram, AiOutlineFacebook, AiOutlineLinkedin } from 'react-icons/ai';
import { motion } from 'framer-motion';
import { useDarkMode } from '../context/DarkModeContext';
import profileImage from '../assets/images/profile1.jpg';

const CEO = () => {
  const { isDark } = useDarkMode();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  const socialLinks = [
    {
      icon: AiOutlineInstagram,
      href: 'https://www.instagram.com/ahtashamspeaks',
      label: 'Instagram',
    },
    {
      icon: AiOutlineFacebook,
      href: 'https://www.facebook.com/share/1VfTmeCBn3',
      label: 'Facebook',
    },
    {
      icon: AiOutlineLinkedin,
      href: 'https://www.linkedin.com/in/ihtasham-nawaz-276759164',
      label: 'LinkedIn',
    },
  ];

  return (
    <section
      id="ceo"
      className={`relative w-full py-20 lg:py-32 transition-colors duration-500 ${
        isDark ? 'bg-dark-primary' : 'bg-light-secondary'
      }`}
    >
      {/* Background Decorations */}
      <motion.div
        className={`absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl -z-10 ${
          isDark ? 'bg-primary-gold/5' : 'bg-primary-gold/8'
        }`}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 6, repeat: Infinity }}
      ></motion.div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-100px' }}
        >
          <motion.p
            className="text-primary-gold font-montserrat font-semibold uppercase tracking-widest text-sm mb-2"
            animate={{ letterSpacing: [0, '0.1em', 0] }}
            transition={{ duration: 3, repeat: Infinity }}
            variants={itemVariants}
          >
            Leadership
          </motion.p>
          <motion.h2
            className={`font-montserrat font-bold text-4xl lg:text-5xl mb-4 transition-colors ${
              isDark ? 'text-white' : 'text-black'
            }`}
            variants={itemVariants}
          >
            Meet Our CEO
          </motion.h2>
          <motion.p
            className={`font-poppins max-w-2xl mx-auto text-lg transition-colors ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
            variants={itemVariants}
          >
            The visionary leading Media Society's mission to transform brands
          </motion.p>
        </motion.div>

        {/* CEO Content */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-100px' }}
        >
          {/* Left - Image */}
          <motion.div
            className="flex justify-center lg:justify-start"
            variants={imageVariants}
          >
            <motion.div
              className={`relative w-80 h-96 rounded-2xl overflow-hidden transition-all ${
                isDark ? 'shadow-gold-glow-lg' : 'shadow-lg'
              }`}
              whileHover={{
                scale: 1.08,
                y: -8,
              }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              {/* Actual Image */}
              <motion.img
                src={profileImage}
                alt="Ahtasham Nawaz CEO"
                className="w-full h-full object-cover rounded-2xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
              />

              {/* Soft shadow frame with glow */}
              <motion.div
                className={`absolute inset-0 rounded-2xl border-2 pointer-events-none`}
                animate={{
                  borderColor: [
                    'rgba(255, 215, 0, 0.2)',
                    'rgba(255, 215, 0, 0.5)',
                    'rgba(255, 215, 0, 0.2)',
                  ],
                  boxShadow: [
                    'inset 0 0 0px rgba(255, 215, 0, 0)',
                    'inset 0 0 20px rgba(255, 215, 0, 0.3)',
                    'inset 0 0 0px rgba(255, 215, 0, 0)',
                  ],
                }}
                transition={{ duration: 3, repeat: Infinity }}
              ></motion.div>
            </motion.div>
          </motion.div>

          {/* Right - CEO Info */}
          <motion.div
            className="space-y-8"
            variants={textVariants}
          >
            {/* Name */}
            <motion.div variants={itemVariants}>
              <motion.p
                className="text-primary-gold font-montserrat font-semibold uppercase tracking-widest text-sm mb-2"
                animate={{ opacity: [0.8, 1, 0.8] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                Founder & CEO
              </motion.p>
              <motion.h3
                className={`font-montserrat font-bold text-4xl lg:text-5xl transition-colors ${
                  isDark ? 'text-white' : 'text-black'
                }`}
              >
                Ahtasham Nawaz
              </motion.h3>
            </motion.div>

            {/* Position */}
            <motion.div
              className={`font-poppins text-lg transition-colors ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}
              variants={itemVariants}
            >
              <p className="font-semibold mb-2">Founder & CEO, Media Society</p>
              <p className={`transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Leading brands to scale their digital presence through strategic social media
                management and influencer marketing.
              </p>
            </motion.div>

            {/* Professional Description */}
            <motion.p
              className={`font-poppins text-base leading-relaxed transition-colors ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}
              variants={itemVariants}
            >
              Ahtasham Nawaz is the founder and CEO of Media Society, leading brands to scale
              their digital presence through strategic social media management and influencer
              marketing. With a strong vision for growth and innovation, he helps businesses
              transform their online presence into powerful revenue-generating platforms.
            </motion.p>

            {/* Mission Statement */}
            <motion.div
              className={`p-6 border-l-4 rounded-lg transition-colors ${
                isDark
                  ? 'bg-primary-gold/10 border-primary-gold/40'
                  : 'bg-primary-gold/15 border-primary-gold/50'
              }`}
              variants={itemVariants}
            >
              <p className={`font-poppins italic text-lg transition-colors ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                "My mission is to help brands turn content into authority, growth, and influence."
              </p>
            </motion.div>

            {/* Social Media Icons */}
            <motion.div className="flex gap-6 pt-4" variants={containerVariants}>
              {socialLinks.map((social, idx) => {
                const IconComponent = social.icon;
                return (
                  <motion.a
                    key={idx}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-14 h-14 rounded-full border-2 flex items-center justify-center text-primary-gold transition-all duration-300 ${
                      isDark
                        ? 'bg-primary-gold/10 border-primary-gold/30 hover:bg-primary-gold hover:text-black hover:border-primary-gold hover:shadow-gold-glow'
                        : 'bg-primary-gold/15 border-primary-gold/40 hover:bg-primary-gold hover:text-black hover:border-primary-gold hover:shadow-lg'
                    }`}
                    whileHover={{ scale: 1.15, rotate: 10, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                    animate={{
                      y: [0, -3, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: idx * 0.2,
                    }}
                    title={social.label}
                    variants={itemVariants}
                    custom={idx}
                  >
                    <IconComponent className="w-6 h-6" />
                  </motion.a>
                );
              })}
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default CEO;
